import React, { useContext, useEffect, useState } from 'react';
import {
  ComponentRendering,
  Field,
  Placeholder,
  RichText,
  SitecoreContext,
  withSitecoreContext,
  WithSitecoreContextProps,
} from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import CreditCard from '../CreditCard/CreditCard';
import { APIWebstoreContext } from '../../context/globalContext';
import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import { ProcessOrderValidationService } from '../CheckoutPage/ProcessOrderValidationService';
import { getCountryConfigFields } from '../../utils/countryConfigurationHelper';
import { PaymentTypes } from '../../utils/PaymentTypes';
import { LOB_LIST } from '../../context/reducer';
import NonInstantPayment from '../NonInstantPayment/NonInstantPayment';
import { ICP_IDENTIFIER, PAYMENT_TYPES_ORDER } from '../../utils/constants';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
type PaymentMethodProps = WithSitecoreContextProps & {
  fields: {
    CCVTooltip: Field<string>;
    WireTransferTooltip: Field<string>;
    CheckTooltip: Field<string>;
    BillingAddressInfo: Field<string>;
    PaymentTypes: Array<PaymentTypesFieldProps>;
    NoCostPaymentMessage: Field<string>;
    Countries: Array<CountryConfigurationFieldProps>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  rendering: ComponentRendering;
  invoiceIdentifier?: string;
};

export type PaymentTypesFieldProps = {
  displayName: string;
  fields: IPaymentTypePropsFields;
};

export interface IPaymentTypePropsFields {
  PaymentTypeName: Field<string>;
  PaymentTypeValue: Field<string>;
  MinimumOrderTotal: Field<number>;
  MaximumOrderTotal: Field<number>;
  'Tooltip Text': Field<string>;
  DisabledToolTipText: Field<string>;
}

export type CountryConfigurationFieldProps = {
  displayName: string;
  fields: ICountryPropsFields;
};

export interface ICountryPropsFields {
  CountryCode: Field<string>;
  HidePaymentType: Field<boolean>;
  CountryName: Field<string>;
  PaymentSectionMessage: Field<string>;
}

const PaymentMethod = ({
  fields,
  sitecoreContext,
  rendering,
  invoiceIdentifier,
}: PaymentMethodProps): React.JSX.Element => {
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [orderTotal, setOrderTotal] = useState<number | undefined>();
  const [invoiceTotal, setInvoiceTotal] = useState<number | undefined>();
  const [selectedPaymentType, setselectedPaymentType] = useState('');
  const [countryConfigurationInfo, setCountryConfigurationInfo] = useState<ICountryPropsFields>();
  const [country, setCountry] = useState('US');
  const [isCCPaymentAllowed, setIsCCPaymentAllowed] = useState<boolean>(true);

  useEffect(() => {
    if (invoiceIdentifier) {
      const billingCountry = state?.groupAdminCountry;

      if (billingCountry) {
        setCountry(billingCountry);
        setCountryConfigurationInfo(getCountrySpecificConfiguration(billingCountry));
      }
    }
  }, [state?.groupAdminCountry]);

  useEffect(() => {
    if (
      Object.values(PaymentTypes).includes(selectedPaymentType as PaymentTypes) &&
      !getPaymentTypes()?.some(
        (type) => type.fields?.PaymentTypeName?.value === selectedPaymentType
      )
    ) {
      setselectedPaymentType('');

      if (dispatch) {
        dispatch({
          type: 'CHECKOUT_UPDATE',
          payload: {
            paymentType: 'None',
            orderDetails: Object.fromEntries(
              Object.entries(state?.orderDetails || {}).map(([key, details]) => [
                key,
                {
                  ...details,
                  paymentType:
                    details.paymentType === selectedPaymentType ? 'None' : details.paymentType,
                },
              ])
            ),
          },
        });
      }
    }
  }, [orderTotal]);

  useEffect(() => {
    ProcessOrderValidationService.setCCInfo({}, false);
    if (dispatch) {
      const orderDetails = Object.fromEntries(
        LOB_LIST.map((lob) => [
          lob,
          {
            paymentType: 'None',
            orderIds: [],
          },
        ])
      );
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          paymentType: 'None',
          orderDetails,
        },
      });
    }
  }, []);

  useEffect(() => {
    setOrderTotal(state?.orderTotals?.totalAmount);
  }, [state?.orderTotals?.totalAmount]);

  useEffect(() => {
    const total = state?.invoicesSummary?.[invoiceIdentifier ?? '']?.totalAmount;
    setInvoiceTotal(total);
  }, [state?.invoicesSummary, invoiceIdentifier]);

  useEffect(() => {
    setselectedPaymentType(selectedPaymentType ?? '');
  }, [state?.paymentType]);

  useEffect(() => {
    if (!invoiceIdentifier) {
      getShippingCountry();
    }
  }, [state?.toggleCalculateOrder]);

  useEffect(() => {
    checkCCPaymentStatus();
  }, [state?.togglePaymentResponse]);

  useEffect(() => {
    if (
      selectedPaymentType === PaymentTypes.CreditCard &&
      !isCCPaymentAllowed &&
      !countryConfigurationInfo?.HidePaymentType?.value
    ) {
      setselectedPaymentType('');

      dispatch?.({
        type: 'CHECKOUT_UPDATE',
        payload: {
          paymentType: 'None',
        },
      });
    }
  }, [countryConfigurationInfo, isCCPaymentAllowed]);

  function getShippingCountry() {
    let shippingCountry: string = '';
    getAPICall(`${process.env.REACT_APP_CART_URL}/checkout/getshippingcountry`)
      .then(function (response: any) {
        if (response && response.data) {
          shippingCountry = response.data;
        }
      })
      .finally(() => {
        if (!shippingCountry && shippingCountry.length === 0) {
          shippingCountry = ProcessOrderValidationService.getShippingInfo()?.shippingInfo?.country;
        }
        setCountryConfigurationInfo(getCountrySpecificConfiguration(shippingCountry));
      });
  }

  function getCountrySpecificConfiguration(shippingCountry: string) {
    let countryConfigurationData: Array<CountryConfigurationFieldProps> = [];
    countryConfigurationData = getCountryConfigFields(shippingCountry, fields?.Countries);
    const countryConfigurationFields: ICountryPropsFields | undefined =
      countryConfigurationData.map((config) => {
        return {
          CountryCode: config?.fields?.CountryCode,
          HidePaymentType: config?.fields?.HidePaymentType,
          CountryName: config?.fields?.CountryName,
          PaymentSectionMessage: config?.fields?.PaymentSectionMessage,
        };
      })[0];
    return countryConfigurationFields;
  }

  function InvoiceSection(type: string, identifier: string) {
    setselectedPaymentType(type);
    if (dispatch) {
      dispatch({
        type: 'INVOICE_UPDATE',
        payload: {
          invoicePayment: {
            [identifier]: type,
          },
        },
      });
    }
  }

  function PaymentSection(type: string) {
    setselectedPaymentType(type);
    if (dispatch) {
      const updatedOrderDetails = Object.fromEntries(
        LOB_LIST.map((lob) => {
          const existing = state?.orderDetails?.[lob];
          const existingPaymentType = Object.values(PaymentTypes).includes(
            existing?.paymentType as PaymentTypes
          )
            ? null
            : existing?.paymentType;
          return [
            lob,
            {
              orderIds: existing?.orderIds ?? [],
              paymentType:
                existingPaymentType && existingPaymentType !== 'None' ? existingPaymentType : type,
            },
          ];
        })
      );
      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          orderDetails: updatedOrderDetails,
          paymentType: type,
        },
      });
    }
  }

  function getPaymentTypes(): Array<PaymentTypesFieldProps> | undefined {
    const total = (orderTotal || invoiceTotal) ?? 0;

    return fields?.PaymentTypes?.filter(
      (type) =>
        total >= (type?.fields?.MinimumOrderTotal?.value ?? 0) &&
        (type?.fields?.MaximumOrderTotal?.value === null ||
          total <= type.fields.MaximumOrderTotal.value)
    ).sort(
      (a, b) =>
        PAYMENT_TYPES_ORDER.indexOf(a.displayName) - PAYMENT_TYPES_ORDER.indexOf(b.displayName)
    );
  }

  function getPaymentTypeHeader() {
    if (
      (orderTotal === 0 && !invoiceIdentifier && !state?.isGroupPaymentSuccessful) ||
      (invoiceIdentifier && invoiceTotal === 0)
    ) {
      if (fields?.NoCostPaymentMessage) {
        return <RichText tag="p" field={fields?.NoCostPaymentMessage} />;
      } else {
        return <p>No Payment Required</p>;
      }
    } else {
      return <p>Select Payment Type</p>;
    }
  }

  function checkCCPaymentStatus() {
    getAPICall(`${process.env.REACT_APP_PROFILE_URL}/profile/payment-eligibility`)
      .then(function (response: any) {
        if (response && response.data) {
          setIsCCPaymentAllowed(response.data.IsCCPaymentAllowed);

          if (dispatch) {
            dispatch({
              type: 'CHECKOUT_UPDATE',
              payload: {
                remainingPaymentAttempts:
                  response.data.FailedAttempts > 0 ? response.data.RemainingAttempts : null,
              },
            });
          }

          if (response.data.IsCCPaymentAllowed === false && dispatch) {
            setselectedPaymentType('');
            if (invoiceIdentifier) {
              dispatch({
                type: 'INVOICE_UPDATE',
                payload: {
                  [invoiceIdentifier]: 'None',
                },
              });
            } else {
              dispatch({
                type: 'CHECKOUT_UPDATE',
                payload: {
                  paymentType: 'None',
                },
              });
            }
          }
        }
      })
      .catch(function (error) {
        console.error('Error fetching CC payment status:', error);
      });
  }

  return (
    <div className={`paymentmethod ${invoiceIdentifier ? 'invoice' : ''}`}>
      {sitecoreContext.pageEditing && (
        <>
          <p>PaymentMethod Component Edit Mode</p>
        </>
      )}
      {!invoiceIdentifier && <hr />}
      <div className="checkoutPaymentSection">
        <div className="paymentArea">
          {!invoiceIdentifier && <div className="SNo">2</div>}

          <div className="Title">
            <p>Payment Method</p>
          </div>
        </div>

        <div className={`paymentType ${invoiceIdentifier ? 'invoice' : ''}`}>
          {getPaymentTypeHeader()}
        </div>

        <div className={`paymentSelection ${invoiceIdentifier ? 'invoice' : ''}`}>
          {getPaymentTypes()?.map((paymentType) => (
            <React.Fragment key={paymentType?.fields?.PaymentTypeName?.value}>
              <div className="radiodiv">
                <input
                  type="radio"
                  id={paymentType?.fields?.PaymentTypeName?.value}
                  name="paymenttype"
                  className="radiobutton"
                  checked={selectedPaymentType === paymentType?.fields?.PaymentTypeName?.value}
                  onChange={() =>
                    invoiceIdentifier
                      ? InvoiceSection(
                          paymentType?.fields?.PaymentTypeName?.value,
                          invoiceIdentifier
                        )
                      : PaymentSection(paymentType?.fields?.PaymentTypeName?.value)
                  }
                  disabled={
                    paymentType?.fields?.PaymentTypeName?.value === PaymentTypes.CreditCard &&
                    !isCCPaymentAllowed &&
                    !countryConfigurationInfo?.HidePaymentType?.value
                  }
                ></input>
                <div className="paymentOptionContainer">
                  <span
                    className="spanpaymenttype"
                    onClick={() =>
                      !(
                        paymentType?.fields?.PaymentTypeName?.value === PaymentTypes.CreditCard &&
                        !isCCPaymentAllowed &&
                        !countryConfigurationInfo?.HidePaymentType?.value
                      ) &&
                      (invoiceIdentifier
                        ? InvoiceSection(
                            paymentType?.fields?.PaymentTypeName?.value,
                            invoiceIdentifier
                          )
                        : PaymentSection(paymentType?.fields?.PaymentTypeName?.value))
                    }
                  >
                    {' '}
                    {paymentType?.fields?.PaymentTypeValue?.value}
                  </span>
                  <CustomTooltip
                    fields={{
                      'Tooltip Text':
                        paymentType?.fields?.PaymentTypeName?.value === PaymentTypes.CreditCard &&
                        !isCCPaymentAllowed &&
                        !countryConfigurationInfo?.HidePaymentType?.value
                          ? paymentType?.fields?.DisabledToolTipText
                          : paymentType?.fields?.['Tooltip Text'],
                    }}
                    disabled={
                      paymentType?.fields?.PaymentTypeName?.value === PaymentTypes.CreditCard &&
                      !isCCPaymentAllowed &&
                      !countryConfigurationInfo?.HidePaymentType?.value
                    }
                  />
                </div>
              </div>
              {selectedPaymentType === PaymentTypes.CreditCard &&
                paymentType?.fields?.PaymentTypeName?.value === PaymentTypes.CreditCard &&
                (countryConfigurationInfo?.HidePaymentType?.value ? (
                  <RichText tag="p" field={countryConfigurationInfo?.PaymentSectionMessage} />
                ) : (
                  isCCPaymentAllowed && (
                    <div className="creditdiv">
                      <CreditCard
                        fields={{
                          CCVTooltip: fields?.CCVTooltip,
                        }}
                        invoiceIdentifier={invoiceIdentifier}
                      ></CreditCard>
                      <br></br>
                    </div>
                  )
                ))}
              {paymentType?.fields?.PaymentTypeName?.value === selectedPaymentType &&
                ((state?.cartCounts?.lobCounts?.ICP ?? 0) > 0 ||
                  invoiceIdentifier === ICP_IDENTIFIER) &&
                selectedPaymentType !== PaymentTypes.CreditCard && (
                  <NonInstantPayment
                    paymentType={selectedPaymentType}
                    invoiceIdentifier={invoiceIdentifier || ''}
                  />
                )}
            </React.Fragment>
          ))}
          {state?.hardCopyQuantity !== null &&
            (state?.cartCounts?.lobCounts?.ICP ?? 0) > 0 &&
            state?.cartCounts?.lobCounts?.ICP !== state?.hardCopyQuantity && (
              <Placeholder name="group_payment" rendering={rendering} />
            )}
        </div>
      </div>
    </div>
  );
};
export default withSitecoreContext()(PaymentMethod);
